// ─── CinematicProduction ───────────────────────────────────────────────────
// Production console for the Deskey Video Studio in the CLEAN, human-designed
// light Deskey theme (no AI-dark theatre look).
//
// Design language:
//   • White card surfaces with soft Deskey borders.
//   • Single amber accent (#F08A24) for the active state.
//   • Calm status line: "Drafting the script · Scene 3 of 8".
//   • Bento grid of scene cards that light up progressively. Each card has
//     three states: pending (hollow outline) → processing (amber pulse) →
//     completed (solid surface).
//   • Cinematic terminology is preserved; the surface itself reads like a
//     friendly SaaS production dashboard rather than a black theatre.
import React, { useMemo } from 'react';
import { Clapperboard } from 'lucide-react';

const ACCENT = '#F08A24';   // Apple amber — sole accent on this surface

// Pipeline phases shown as a calm status line — no fake marquee rotation.
const PHASES = [
  { id: 'script', label: 'Drafting the script',     pct: 0.10 },
  { id: 'cast',   label: 'Casting voices',          pct: 0.18 },
  { id: 'shoot',  label: 'Capturing scenes',        pct: 0.50 },
  { id: 'score',  label: 'Recording the score',     pct: 0.75 },
  { id: 'master', label: 'Mastering the final cut', pct: 0.95 },
];

export default function CinematicProduction({
  elapsedSeconds = 0,
  mode = 'explain',
  lessonTitle = '',
  genreLabel = '',
  panelCount = 8,
  estimatedSeconds = 120,
}) {
  // Progress ratio for the WHOLE film (sweep from 0 → 1 over the estimate).
  const progress = Math.min(1, Math.max(0, elapsedSeconds / Math.max(60, estimatedSeconds)));
  const totalPanels = Math.max(6, Math.min(16, panelCount || 8));

  const panels = useMemo(() => {
    const completed = Math.floor(progress * totalPanels);
    return Array.from({ length: totalPanels }, (_, i) => ({
      index: i + 1,
      state: i < completed ? 'completed'
           : i === completed ? 'processing'
           : 'pending',
    }));
  }, [progress, totalPanels]);

  const currentPhase = useMemo(() => {
    for (let i = PHASES.length - 1; i >= 0; i--) {
      if (progress >= PHASES[i].pct) return PHASES[i];
    }
    return PHASES[0];
  }, [progress]);

  const currentSceneNum = Math.min(totalPanels, Math.floor(progress * totalPanels) + 1);
  const tc = formatTimecode(elapsedSeconds);
  const modeLabel = mode === 'story' ? 'Narrative' : 'Explainer';

  return (
    <div
      data-testid="cinematic-production"
      className="relative overflow-hidden rounded-2xl border"
      style={{ background: 'var(--st-card)', borderColor: 'var(--st-border)' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="px-6 sm:px-10 pt-8 pb-5">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2 text-xs font-semibold "
            style={{ color: 'var(--st-mute)' }}>
            <Clapperboard className="w-3.5 h-3.5" style={{ color: ACCENT }} />
            Deskey Studios · {modeLabel}{genreLabel ? ` · ${genreLabel}` : ''}
          </div>
          <div className="font-mono text-xs font-semibold tabular-nums "
            style={{ color: 'var(--st-mute)' }}
            data-testid="production-timecode">
            {tc}
          </div>
        </div>

        <h1 className="text-[24px] sm:text-[30px] font-semibold leading-[1.1] max-w-2xl"
          style={{ fontFamily: 'var(--st-font-heading)', color: 'var(--st-ink)' }}>
          {lessonTitle || 'Your film is now in production.'}
        </h1>
        <p className="mt-2 text-sm leading-relaxed max-w-xl"
          style={{ color: 'var(--st-mute)' }}>
          The film is being produced in the background. Step away anytime —
          your premiere will be waiting in the Library when it's ready.
        </p>
      </div>

      {/* ── Status line + progress meter ───────────────────────────────── */}
      <div className="px-6 sm:px-10 pb-6">
        <div className="flex items-baseline justify-between gap-3 mb-2.5">
          <div data-testid="production-status-line"
            className="text-sm font-semibold inline-flex items-center gap-2"
            style={{ color: 'var(--st-ink)' }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full"
              style={{
                background: ACCENT,
                boxShadow:  `0 0 0 4px ${ACCENT}22`,
              }} />
            <span>{currentPhase.label}</span>
            <span style={{ color: 'var(--st-mute)' }}>
              · Scene {currentSceneNum} of {totalPanels}
            </span>
          </div>
          <div className="font-mono text-xs font-semibold tabular-nums"
            style={{ color: 'var(--st-mute)' }}>
            {Math.round(progress * 100)}%
          </div>
        </div>
        <div className="relative h-[3px] w-full rounded-full overflow-hidden"
          style={{ background: 'var(--st-bg)', border: '1px solid var(--st-border)' }}>
          <div className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width:      `${progress * 100}%`,
              background: `linear-gradient(90deg, ${ACCENT}, color-mix(in srgb, ${ACCENT} 65%, #fff))`,
              transition: 'width 700ms cubic-bezier(0.32, 0.72, 0, 1)',
              boxShadow:  `0 0 10px ${ACCENT}66`,
            }} />
        </div>
      </div>

      {/* ── Bento grid of scene cards ──────────────────────────────────── */}
      <div className="px-6 sm:px-10 pb-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {panels.map(p => (
            <SceneCard key={p.index} {...p} />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes cpBreath {
          0%, 100% { opacity: 0.85; transform: scale(1);    }
          50%      { opacity: 1;    transform: scale(1.015); }
        }
      `}</style>
    </div>
  );
}

// ─── Scene Card ────────────────────────────────────────────────────────────
function SceneCard({ index, state }) {
  const isDone       = state === 'completed';
  const isProcessing = state === 'processing';

  const baseStyle = {
    aspectRatio: '16 / 10',
    borderRadius: 14,
    transitionTimingFunction: 'cubic-bezier(0.32, 0.72, 0, 1)',
  };
  let style = { ...baseStyle };
  let label;
  if (isDone) {
    style = { ...style,
      background:  `linear-gradient(135deg, color-mix(in srgb, ${ACCENT} 12%, var(--st-card)) 0%, var(--st-card) 100%)`,
      border:      `1px solid color-mix(in srgb, ${ACCENT} 40%, var(--st-border))`,
      boxShadow:   `0 12px 28px -18px ${ACCENT}88`,
    };
    label = (
      <>
        <div className="absolute top-3 left-3 font-mono text-xs font-semibold tabular-nums"
          style={{ color: 'var(--st-mute)' }}>
          SC {String(index).padStart(2, '0')}
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
          <span className="text-xs font-semibold "
            style={{ color: ACCENT }}>
            Captured
          </span>
          <span className="w-1.5 h-1.5 rounded-full"
            style={{ background: ACCENT, boxShadow: `0 0 0 3px ${ACCENT}22` }} />
        </div>
      </>
    );
  } else if (isProcessing) {
    style = { ...style,
      background:  `color-mix(in srgb, ${ACCENT} 7%, var(--st-card))`,
      border:      `1.5px solid ${ACCENT}`,
      boxShadow:   `0 0 0 4px ${ACCENT}1f, 0 16px 32px -16px ${ACCENT}66`,
      animation:   'cpBreath 2.4s ease-in-out infinite',
    };
    label = (
      <>
        <div className="absolute top-3 left-3 font-mono text-xs font-semibold tabular-nums"
          style={{ color: ACCENT }}>
          SC {String(index).padStart(2, '0')}
        </div>
        <div className="absolute bottom-3 left-3 right-3 inline-flex items-center gap-1.5">
          <span className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: ACCENT, animation: 'cpBreath 1.4s ease-in-out infinite' }} />
          <span className="text-xs font-semibold "
            style={{ color: ACCENT }}>
            Mastering…
          </span>
        </div>
      </>
    );
  } else {
    style = { ...style,
      background:  'var(--st-bg)',
      border:      '1px dashed var(--st-border)',
    };
    label = (
      <div className="absolute top-3 left-3 font-mono text-xs font-semibold tabular-nums"
        style={{ color: 'var(--st-mute)', opacity: 0.6 }}>
        SC {String(index).padStart(2, '0')}
      </div>
    );
  }
  return (
    <div
      data-testid={`production-scene-${index}`}
      data-state={state}
      className="relative transition duration-300 "
      style={style}>
      {label}
    </div>
  );
}

function formatTimecode(seconds) {
  const total = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  const ss = total % 60;
  const p2 = n => n.toString().padStart(2, '0');
  return `${p2(hh)}:${p2(mm)}:${p2(ss)}`;
}
